import type { DateTime } from 'luxon';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { PlatformTypes } from '@/types/gql.generated';

type EventLike = {
  id: string;
  startDate: DateTime;
};

export type EntryExport = {
  entry: EventLike;
  externalLink: string;
  /** Indicates that the entry has been updated after it was exported */
  isStale: boolean;
  platform: PlatformTypes;
  destinationEntry: EventLike | null;
};

export type PlatformExport = {
  platform: PlatformTypes;
  integrationId: string;
  calendarId: string;
  entryExports: EntryExport[];
};

type State = {
  addExport: (item: PlatformExport) => void;
  invalidateEntryExports: (entry: EventLike) => void;
  platformExports: PlatformExport[];
  reset: () => void;
};

const getStartDate = (entry: EventLike): Date => {
  return entry.startDate.toJSDate();
};

export const usePlatformExports = createWithEqualityFn<State>()(
  (set) => ({
    platformExports: [],
    addExport: (platformExport) => {
      set((state) => {
        const existing = state.platformExports.find((item) => {
          return (
            item.platform === platformExport.platform &&
            item.integrationId === platformExport.integrationId &&
            item.calendarId === platformExport.calendarId
          );
        });

        if (!existing) {
          return {
            platformExports: [...state.platformExports, platformExport],
          };
        }

        return {
          platformExports: state.platformExports.map((item) => {
            if (
              item.platform !== existing.platform ||
              item.integrationId !== existing.integrationId ||
              item.calendarId !== existing.calendarId
            ) {
              return item;
            }

            const updatedEntryExports = existing.entryExports.map(
              (entryExport): EntryExport => {
                const incomingExport = platformExport.entryExports.find(
                  (incoming) => incoming.entry.id === entryExport.entry.id
                );

                if (!incomingExport) {
                  return entryExport;
                }

                return {
                  ...entryExport,
                  entry: { ...entryExport.entry, ...incomingExport.entry },
                  isStale: false,
                };
              }
            );

            const newEntryExports = platformExport.entryExports.filter(
              (item) =>
                !existing.entryExports.some(
                  (existingItem) => existingItem.entry.id === item.entry.id
                )
            );

            return {
              ...platformExport,
              entryExports: [...updatedEntryExports, ...newEntryExports].sort(
                (a, b) => {
                  const aStartAt = getStartDate(a.entry);
                  const bStartAt = getStartDate(b.entry);

                  return aStartAt > bStartAt ? 1 : -1;
                }
              ),
            };
          }),
        };
      });
    },
    invalidateEntryExports: (entry) => {
      set((state) => {
        const updatedPlatformExports = state.platformExports.map(
          (platformExport) => {
            return {
              ...platformExport,
              entryExports: platformExport.entryExports.map((item) => {
                if (item.entry.id === entry.id) {
                  return {
                    ...item,
                    isStale: true,
                  };
                }
                return item;
              }),
            };
          }
        );

        return {
          platformExports: updatedPlatformExports,
        };
      });
    },
    reset: () => {
      set({ platformExports: [] });
    },
  }),
  shallow
);
